import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { useConfirm } from "../context/ConfirmDialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "../components/ui/drawer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ShieldCheck,
  Check,
  Ban,
  Clock,
  Search,
  ChevronRight,
  ArrowLeft,
  Package,
  RefreshCw,
  Sliders,
  Users as UsersIcon,
  Home as HomeIcon,
  Briefcase,
} from "lucide-react";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso) - new Date();
    return Math.ceil(ms / 86400000);
  } catch { return null; }
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, formatDateTime } = useT();
  const confirm = useConfirm();
  const [tab, setTab] = useState("users"); // users | plans
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pantry, setPantry] = useState(null);
  const [busy, setBusy] = useState(false);

  const STATUS_META = {
    super_admin: { label: t("admin.status.super_admin"), dot: "bg-zinc-950", text: "text-zinc-950", bg: "bg-zinc-100" },
    approved: { label: t("admin.status.approved"), dot: "bg-fresh-green", text: "text-fresh-green-text", bg: "bg-fresh-green-soft" },
    trial: { label: t("admin.status.trial"), dot: "bg-fresh-yellow", text: "text-fresh-yellow-text", bg: "bg-fresh-yellow-soft" },
    expired: { label: t("admin.status.expired"), dot: "bg-fresh-red", text: "text-fresh-red-text", bg: "bg-fresh-red-soft" },
    disabled: { label: t("admin.status.disabled"), dot: "bg-zinc-500", text: "text-zinc-600", bg: "bg-zinc-100" },
  };

  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin.toast.unauthorized"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.household_name || "").toLowerCase().includes(q)
      );
    });
  }, [users, filter, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: users.length, approved: 0, trial: 0, expired: 0, disabled: 0, super_admin: 0 };
    for (const u of users) c[u.status] = (c[u.status] || 0) + 1;
    return c;
  }, [users]);

  const openUser = async (u) => {
    setSelected(u);
    setPantry(null);
    try {
      const r = await api.get(`/admin/users/${u.user_id}/pantry`);
      setPantry(r.data);
    } catch (_e) {
      toast.error(t("dashboard.toast.cant_load"));
    }
  };

  const closeDrawer = () => { setSelected(null); setPantry(null); };

  const approve = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${selected.user_id}/approve`);
      toast.success(t("admin.toast.approved", { email: selected.email }));
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: t("admin.confirm.disable_title"),
      description: t("admin.confirm.disable", { email: selected.email }),
      confirmLabel: t("admin.action.disable"),
      cancelLabel: t("common.cancel"),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${selected.user_id}/disable`);
      toast.success(t("admin.toast.disabled", { email: selected.email }));
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const extend = async (days) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${selected.user_id}/extend?days=${days}`);
      toast.success(t("admin.toast.extended", { n: days }));
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const setAccountType = async (val) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.patch(`/admin/users/${selected.user_id}`, { account_type: val });
      toast.success("OK");
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white pb-20" data-testid="admin-page">
      <header className="pt-safe pt-10 px-6 border-b border-zinc-100 pb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-green-text flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> {t("admin.eyebrow")}
            </div>
            <h1 className="font-heading text-3xl font-medium leading-tight">{t("admin.title")}</h1>
          </div>
          {tab === "users" && (
            <button onClick={load} className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center active:scale-90 transition" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-5 grid grid-cols-2 bg-zinc-100 rounded-xl p-1 h-12">
          <button
            onClick={() => setTab("users")}
            className={`rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition ${tab === "users" ? "bg-white shadow-sm" : "text-zinc-500"}`}
            data-testid="admin-tab-users"
          >
            <UsersIcon className="w-4 h-4" /> {t("admin.tab.users")}
          </button>
          <button
            onClick={() => setTab("plans")}
            className={`rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition ${tab === "plans" ? "bg-white shadow-sm" : "text-zinc-500"}`}
            data-testid="admin-tab-plans"
          >
            <Sliders className="w-4 h-4" /> {t("admin.tab.plans")}
          </button>
        </div>

        {tab === "users" && (
          <>
            <div className="mt-5 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t("admin.search.placeholder")}
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-zinc-50 border border-zinc-200 text-sm outline-none focus:border-zinc-400"
                data-testid="admin-search"
              />
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-filter-chips">
              {["all", "trial", "expired", "approved", "disabled", "super_admin"].map((k) => {
                const label = k === "all" ? t("admin.filter.all") : STATUS_META[k]?.label || k;
                const active = statusFilter === k;
                return (
                  <button
                    key={k}
                    onClick={() => setStatusFilter(k)}
                    className={`shrink-0 h-9 px-4 rounded-full text-xs font-semibold border transition whitespace-nowrap ${active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"}`}
                    data-testid={`admin-filter-${k}`}
                  >
                    {label}
                    <span className={`ml-1.5 text-[10px] ${active ? "text-white/70" : "text-zinc-400"}`}>{counts[k] ?? 0}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </header>

      {tab === "users" && (
        <section className="pt-2" data-testid="admin-users-list">
          {loading ? (
            <div className="text-center py-12 text-zinc-400 text-sm">{t("common.loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-sm">{t("admin.no_users")}</div>
          ) : (
            filtered.map((u) => (
              <button
                key={u.user_id}
                onClick={() => openUser(u)}
                className="w-full flex items-center gap-3 px-6 py-4 border-b border-zinc-100 text-left active:bg-zinc-50 transition"
                data-testid={`admin-row-${u.user_id}`}
              >
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-11 h-11 rounded-full shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-zinc-100 shrink-0 flex items-center justify-center text-zinc-500 font-heading font-semibold">
                    {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-base font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-500">
                    <StatusPill status={u.status} meta={STATUS_META} />
                    {u.account_type && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 flex items-center gap-1">
                        {u.account_type === "business" ? <Briefcase className="w-3 h-3" /> : <HomeIcon className="w-3 h-3" />}
                        {u.account_type === "business" ? t("settings.plan.business") : t("settings.plan.private")}
                      </span>
                    )}
                    <span>·</span>
                    <span className="flex items-center gap-1"><Package className="w-3 h-3" />{u.items_count}</span>
                    {u.household_name && <span className="truncate">· {u.household_name}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />
              </button>
            ))
          )}
        </section>
      )}

      {tab === "plans" && <PlansTab t={t} />}

      <Drawer open={!!selected} onOpenChange={(o) => !o && closeDrawer()}>
        <DrawerContent data-testid="admin-user-drawer">
          {selected && (
            <div className="mx-auto w-full max-w-md text-zinc-950">
              <DrawerHeader className="text-left">
                <div className="flex items-center gap-3">
                  {selected.picture ? (
                    <img src={selected.picture} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 font-heading font-semibold">
                      {(selected.name || selected.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <DrawerTitle className="font-heading text-xl font-medium truncate">{selected.name || selected.email}</DrawerTitle>
                    <DrawerDescription className="truncate">{selected.email}</DrawerDescription>
                  </div>
                </div>
              </DrawerHeader>
              <div className="px-4 max-h-[65vh] overflow-y-auto" data-testid="admin-user-details">
                <div className="rounded-2xl border border-zinc-200 p-4 space-y-3">
                  <Row label={t("admin.user.status")}><StatusPill status={selected.status} meta={STATUS_META} /></Row>
                  <Row label={t("admin.user.household")}>{selected.household_name || "—"}</Row>
                  <Row label={t("admin.user.created")}>{formatDateTime(selected.created_at)}</Row>
                  <Row label={t("admin.user.trial_until")}>
                    {selected.activated_until ? (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-zinc-400" />
                        <span>{formatDateTime(selected.activated_until)}</span>
                        {(() => {
                          const d = daysUntil(selected.activated_until);
                          if (d == null) return null;
                          return (
                            <span className={`ml-1 text-[10px] font-bold ${d < 0 ? "text-fresh-red-text" : d <= 2 ? "text-fresh-yellow-text" : "text-zinc-500"}`}>
                              {d < 0 ? t("common.expired_days", { n: -d }) : t("common.days_left", { n: d })}
                            </span>
                          );
                        })()}
                      </div>
                    ) : t("admin.user.permanent")}
                  </Row>
                  <Row label={t("admin.row.products_active")}>{selected.items_count}</Row>
                </div>

                {selected.status !== "super_admin" && (
                  <div className="mt-5 rounded-2xl border border-zinc-200 p-4">
                    <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">{t("settings.plan.title")}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "private", label: t("settings.plan.private"), Icon: HomeIcon },
                        { v: "business", label: t("settings.plan.business"), Icon: Briefcase },
                      ].map(({ v, label, Icon }) => {
                        const active = selected.account_type === v;
                        return (
                          <button
                            key={v}
                            onClick={() => setAccountType(v)}
                            disabled={busy || active}
                            className={`h-10 rounded-xl text-xs font-bold border transition flex items-center justify-center gap-1.5 ${active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"}`}
                            data-testid={`admin-set-plan-${v}`}
                          >
                            <Icon className="w-3.5 h-3.5" /> {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-5">
                  <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">{t("admin.section.pantry")}</div>
                  {!pantry ? (
                    <div className="text-sm text-zinc-400">{t("common.loading")}</div>
                  ) : pantry.count === 0 ? (
                    <div className="text-sm text-zinc-400">{t("admin.section.empty")}</div>
                  ) : (
                    <div className="space-y-3">
                      {["red", "yellow", "green", "expired"].map((k) => {
                        const items = pantry.grouped[k] || [];
                        if (!items.length) return null;
                        const meta = {
                          red: { label: t("dashboard.stats.critical"), tint: "text-fresh-red-text" },
                          yellow: { label: t("dashboard.stats.soon"), tint: "text-fresh-yellow-text" },
                          green: { label: t("dashboard.stats.fresh"), tint: "text-fresh-green-text" },
                          expired: { label: t("dashboard.stats.expired"), tint: "text-zinc-600" },
                        }[k];
                        return (
                          <div key={k}>
                            <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${meta.tint}`}>{meta.label} · {items.length}</div>
                            {items.slice(0, 30).map((it) => (
                              <div key={it.item_id} className="flex items-center gap-2 text-sm py-1.5 border-b border-zinc-100 last:border-0" data-testid={`admin-item-${it.item_id}`}>
                                <div className="flex-1 min-w-0 truncate">{it.product_name}</div>
                                <div className="text-xs text-zinc-500">{t("common.qty")} {it.quantity}</div>
                                <div className="text-xs text-zinc-400 w-24 text-right">{it.expiry_date}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <DrawerFooter className="gap-2">
                {selected.status !== "super_admin" && (
                  <>
                    {selected.status !== "approved" && (
                      <Button onClick={approve} disabled={busy} className="w-full h-12 rounded-xl bg-fresh-green text-black font-bold" data-testid="admin-approve-btn">
                        <Check className="w-4 h-4 mr-2" strokeWidth={3} /> {t("admin.action.approve")}
                      </Button>
                    )}
                    <div className="flex gap-2">
                      <Button onClick={() => extend(7)} disabled={busy} variant="secondary" className="flex-1 h-11 rounded-xl bg-zinc-100 text-zinc-950" data-testid="admin-extend-7">
                        {t("admin.action.extend7")}
                      </Button>
                      <Button onClick={() => extend(30)} disabled={busy} variant="secondary" className="flex-1 h-11 rounded-xl bg-zinc-100 text-zinc-950" data-testid="admin-extend-30">
                        {t("admin.action.extend30")}
                      </Button>
                    </div>
                    {selected.status !== "disabled" && (
                      <Button onClick={disable} disabled={busy} variant="secondary" className="w-full h-11 rounded-xl bg-fresh-red-soft text-fresh-red-text font-bold" data-testid="admin-disable-btn">
                        <Ban className="w-4 h-4 mr-2" /> {t("admin.action.disable")}
                      </Button>
                    )}
                  </>
                )}
                {selected.status === "super_admin" && (
                  <div className="text-center text-xs text-zinc-500">
                    <ShieldCheck className="w-4 h-4 inline mr-1" /> {t("admin.super_admin_locked")}
                  </div>
                )}
              </DrawerFooter>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}

function PlansTab({ t }) {
  const [plans, setPlans] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/plans");
      setPlans(r.data.plans);
      setDefaults(r.data.defaults);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!plans) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/plans", { private: plans.private, business: plans.business });
      setPlans(r.data.plans);
      toast.success(t("admin.plans.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const reset = () => { if (defaults) setPlans(JSON.parse(JSON.stringify(defaults))); };

  if (!plans) return <div className="px-6 py-12 text-center text-zinc-400 text-sm">{t("common.loading")}</div>;

  const PlanCard = ({ id, title, Icon }) => (
    <div className="rounded-3xl border border-zinc-200 p-5" data-testid={`plans-card-${id}`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-zinc-950 text-white flex items-center justify-center"><Icon className="w-5 h-5" /></div>
        <div className="font-heading text-xl font-medium">{title}</div>
      </div>
      <div className="mt-5 space-y-3">
        <NumberRow
          label={t("admin.plans.field.households")}
          value={plans[id].max_households}
          onChange={(v) => setPlans((p) => ({ ...p, [id]: { ...p[id], max_households: v } }))}
          testId={`plans-${id}-households`}
        />
        <NumberRow
          label={t("admin.plans.field.members")}
          value={plans[id].max_members_per_household}
          onChange={(v) => setPlans((p) => ({ ...p, [id]: { ...p[id], max_members_per_household: v } }))}
          testId={`plans-${id}-members`}
        />
        <NumberRow
          label={t("admin.plans.field.items")}
          value={plans[id].max_items}
          onChange={(v) => setPlans((p) => ({ ...p, [id]: { ...p[id], max_items: v } }))}
          testId={`plans-${id}-items`}
        />
      </div>
    </div>
  );

  return (
    <section className="px-6 pt-6 space-y-5" data-testid="admin-plans-tab">
      <div>
        <h2 className="font-heading text-2xl font-medium">{t("admin.plans.title")}</h2>
        <p className="mt-1 text-sm text-zinc-500">{t("admin.plans.subtitle")}</p>
      </div>
      <PlanCard id="private" title={t("settings.plan.private")} Icon={HomeIcon} />
      <PlanCard id="business" title={t("settings.plan.business")} Icon={Briefcase} />
      <div className="flex gap-2 pt-2">
        <Button onClick={reset} variant="secondary" className="flex-1 h-12 rounded-xl bg-zinc-100 text-zinc-700" data-testid="plans-reset-btn">
          {t("admin.plans.reset")}
        </Button>
        <Button onClick={save} disabled={busy} className="flex-1 h-12 rounded-xl bg-zinc-950 text-white font-bold" data-testid="plans-save-btn">
          {busy ? t("common.loading") : t("admin.plans.save")}
        </Button>
      </div>
    </section>
  );
}

function NumberRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 text-sm text-zinc-700">{label}</div>
      <Input
        type="number"
        min={1}
        value={value}
        onChange={(e) => {
          const n = Math.max(1, Math.min(999999, parseInt(e.target.value || "1", 10)));
          onChange(Number.isFinite(n) ? n : 1);
        }}
        className="w-24 h-10 text-right font-mono"
        data-testid={testId}
      />
    </div>
  );
}

function StatusPill({ status, meta }) {
  const m = meta[status] || meta.approved;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider w-32 shrink-0 pt-0.5">{label}</span>
      <span className="flex-1 text-right text-zinc-950">{children}</span>
    </div>
  );
}
